/**
 * providerGuides.js
 * -----------------
 * Per-provider step guides shown in the AddNewAccount right-hand panel.
 *
 * Structure per entry:
 *   type        – "Social Media" | "Email" | "SMS"
 *   color       – brand colour for the step-number circles
 *   title       – display name shown as the panel heading
 *   description – one-line summary shown under the heading
 *   pdfUrl     – (optional) path to a PDF guide, e.g. "/guides/oruclicks-facebook-setup.pdf"
 *                 When present, a "View Full PDF Guide" button appears at the top of the panel.
 *   steps       – array of { step, field, tip }
 *                 step  → display number ("1", "2", …)
 *                 field → bold field label
 *                 tip   → grey helper sentence
 *
 * Leave steps: [] until you are ready to fill in the real content.
 * Steps will show a "Coming soon" notice while the array is empty.
 */

export const PROVIDER_GUIDES = {

  // ── Social Media ─────────────────────────────────────────────────────────

  Facebook: {
    type: "Social Media",
    color: "#1877F2",
    title: "Facebook",
    description: "Connect your Facebook Page or Profile to schedule and publish posts.",
    pdfUrl: "https://oruclicks-setup-docs.s3.ap-south-1.amazonaws.com/Oruclicks+-+SocialMedia+SetUp+(Facebook).pdf",
    steps: [
      {
        step: "1",
        field: "Go to Meta Developer Portal",
        tip: "Open https://developers.facebook.com/apps/ and log in with your Facebook account.",
      },
      {
        step: "2",
        field: 'Click "Create App"',
        tip: "Click Create App, choose the Business app type, then click Next.",
      },
      {
        step: "3",
        field: "Fill App Details",
        tip: "Enter an App Name (e.g. OruClick Social Manager), your contact email, and optionally your Business Account. Click Create App.",
      },
      {
        step: "4",
        field: "Add Products to the App",
        tip: "From the App Dashboard click Set Up beside the products you need: Facebook Login, Graph API, Pages API, Instagram Graph API, and/or Webhooks.",
      },
      {
        step: "5",
        field: "Configure Basic Settings",
        tip: "Go to Settings → Basic. Fill in Privacy Policy URL, Terms of Service URL, App Domain, upload an App Logo, and add your Website (e.g. https://www.oruclicks.com).",
      },
      {
        step: "6",
        field: "Add Required Permissions",
        tip: "Go to App Review → Permissions and Features. Request: pages_manage_posts, pages_read_engagement, pages_show_list, instagram_basic, instagram_content_publish, business_management. Meta must approve these before going live.",
      },
      {
        step: "7",
        field: "Generate Access Token",
        tip: "Open the Graph API Explorer (https://developers.facebook.com/tools/explorer/), select your App, click Generate Access Token, select the required permissions, then copy the token and paste it in the Access Token field above.",
      },
      {
        step: "8",
        field: "Switch App to Live Mode",
        tip: "Go to App Settings → Basic and toggle App Mode from Development to Live. This allows public users to connect their accounts.",
      },
    ],
  },

  Instagram: {
    type: "Social Media",
    color: "#E1306C",
    title: "Instagram",
    description: "Connect your Instagram Business or Creator account.",
    pdfUrl: "https://oruclicks-setup-docs.s3.ap-south-1.amazonaws.com/Oruclicks+-+SocialMedia+SetUp+(Instagram).pdf",
    steps: [
      // TODO: Add your Instagram-specific steps here.
    ],
  },

  LinkedIn: {
    type: "Social Media",
    color: "#0A66C2",
    title: "LinkedIn",
    description: "Connect your LinkedIn Company Page or personal Profile.",
    pdfUrl: "https://oruclicks-setup-docs.s3.ap-south-1.amazonaws.com/Oruclicks+-+SocialMedia+SetUp+(LinkedIn).pdf",
    steps: [
      // TODO: Add your LinkedIn-specific steps here.
    ],
  },

  // ── Email (Phase 2) ───────────────────────────────────────────────────────

  SendGrid: {
    type: "Email",
    color: "#1A82E2",
    title: "SendGrid",
    description: "Connect SendGrid for transactional and marketing email delivery.",
    steps: [
      // TODO: Add your SendGrid-specific steps here.
    ],
  },

  Mailchimp: {
    type: "Email",
    color: "#FFE01B",
    title: "Mailchimp",
    description: "Connect Mailchimp for email marketing campaigns.",
    steps: [
      // TODO: Add your Mailchimp-specific steps here.
    ],
  },

  "Gmail SMTP": {
    type: "Email",
    color: "#EA4335",
    title: "Gmail SMTP",
    description: "Send emails through your Gmail account via SMTP.",
    steps: [
      // TODO: Add your Gmail SMTP-specific steps here.
    ],
  },

  "Outlook SMTP": {
    type: "Email",
    color: "#0078D4",
    title: "Outlook SMTP",
    description: "Send emails through Microsoft Outlook via SMTP.",
    steps: [
      // TODO: Add your Outlook SMTP-specific steps here.
    ],
  },

  "AWS SES": {
    type: "Email",
    color: "#FF9900",
    title: "AWS SES",
    description: "Send emails via Amazon Simple Email Service.",
    steps: [
      // TODO: Add your AWS SES-specific steps here.
    ],
  },

  // ── SMS (Phase 2) ─────────────────────────────────────────────────────────

  Twilio: {
    type: "SMS",
    color: "#F22F46",
    title: "Twilio",
    description: "Connect Twilio to send SMS messages.",
    steps: [
      // TODO: Add your Twilio-specific steps here.
    ],
  },

  "AWS SNS": {
    type: "SMS",
    color: "#FF9900",
    title: "AWS SNS",
    description: "Send SMS via Amazon Simple Notification Service.",
    steps: [
      // TODO: Add your AWS SNS-specific steps here.
    ],
  },

  Nexmo: {
    type: "SMS",
    color: "#00B0DC",
    title: "Nexmo (Vonage)",
    description: "Connect Vonage/Nexmo for SMS messaging.",
    steps: [
      // TODO: Add your Nexmo-specific steps here.
    ],
  },

  TextMagic: {
    type: "SMS",
    color: "#2D7DD2",
    title: "TextMagic",
    description: "Connect TextMagic for bulk SMS messaging.",
    steps: [
      // TODO: Add your TextMagic-specific steps here.
    ],
  },

  ClickSend: {
    type: "SMS",
    color: "#00AABB",
    title: "ClickSend",
    description: "Connect ClickSend for SMS and messaging services.",
    steps: [
      // TODO: Add your ClickSend-specific steps here.
    ],
  },
};
